/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNodesState, useEdgesState, addEdge } from "reactflow";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { Chip, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PtwSettingsData } from "./workFlow-context";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

const StyledNode = styled("div")(({ theme, isRoot, isDarkMode }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: isRoot
    ? theme.palette.primary.main
    : isDarkMode
      ? theme.palette.grey[900]
      : theme.palette.grey[100],
  color: theme.palette.getContrastText(
    isRoot
      ? theme.palette.primary.main
      : isDarkMode
        ? theme.palette.grey[900]
        : theme.palette.grey[100]
  ),
  borderRadius: "16px",
  border: `2px solid ${
    isRoot
      ? theme.palette.primary.dark
      : isDarkMode
        ? theme.palette.grey[700]
        : theme.palette.grey[300]
  }`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: theme.shadows[4],
  position: "relative",
  padding: theme.spacing(2),
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[8],
  },
}));

const useWorkflowData = (onDeleteStep) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { userGroupsList, inputTypeList, refetchWorkflows } = PtwSettingsData();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();

  // State for modal, selected node, and new action information
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [userGroupIds, setUserGroupIds] = useState([]);
  const [modalButton, setModalButton] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(theme.palette.mode === "dark");

  useEffect(() => {
    setIsDarkMode(theme.palette.mode === "dark");
  }, [theme.palette.mode, theme]);

  const {
    mutate: postData,
    isPending: postIsLoading,
    error: postError,
  } = usePostJsonData();
  const { setItem, getItem } = useLocalStorage("workFlow");

  // Initialize nodeData with self_actionable defaulting to false and options array
  const [nodeData, setNodeData] = useState({
    action_name: "",
    status: "",
    self_actionable: false,
    userGroupIds: [],
    step_inputs: [],
  });

  // Default workflow data structure with self_actionable false and options on the Create step
  const defaultWorkFlow = {
    active: true,
    id: uuidv4(),
    name: "",
    target: "WORK_PERMIT",
    steps: [
      {
        id: uuidv4(),
        action_name: t("workflowData.createActionName"),
        status: t("workflowData.createdStatus"),
        self_actionable: false,
        parent_id: null,
        step_inputs: [],
        userGroupIds: [],
      },
    ],
  };
  const [workflowData, setWorkflowData] = useState(
    getItem("workFlow") || defaultWorkFlow
  );

  // Node and edge states for ReactFlow
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    setItem(workflowData);
  }, [workflowData, setItem]);

  // Handle opening and closing the modal
  const openUpdateModal = (nodeId) => {
    setModalButton("update");
    const selectedNode = workflowData.steps.find((step) => step.id === nodeId);
    setSelectedNode(nodeId);

    if (!selectedNode) return;

    setNodeData({
      action_name: selectedNode.action_name,
      status: selectedNode.status,
      self_actionable: selectedNode.self_actionable,
      step_inputs: Array.isArray(selectedNode.step_inputs)
        ? selectedNode.step_inputs.map((input) => ({
            ...input,
            options: input.options || [],
          }))
        : [],
    });

    if (userGroupsList?.length) {
      setUserGroupIds(
        selectedNode.userGroupIds.map((id) => ({
          value: id,
          label:
            userGroupsList.find((group) => group.value === id)?.label ||
            t("workflowData.unknownLabel"),
        }))
      );
    } else {
      setUserGroupIds([]);
    }

    setIsModalOpen(true);
  };

  const openModal = (nodeId) => {
    setModalButton("create");
    setSelectedNode(nodeId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedNode(null);
    setNodeData({
      action_name: "",
      status: "",
      self_actionable: false,
      userGroupIds: [],
      step_inputs: [],
    });
    setUserGroupIds([]);
  };

  // Function to handle multi-select changes for user groups
  const handleMultiChange = (field) => (selectedOptions) => {
    if (field === "user_groups") {
      setUserGroupIds(selectedOptions);
    }
  };

  const changeTarget = (newTarget) => {
    setWorkflowData((prevData) => ({
      ...prevData,
      target: newTarget,
    }));
  };

  // Function to update node data (editing an existing step)
  const updateNodeData = () => {
    if (!selectedNode) return;

    const updatedWorkflowData = {
      ...workflowData,
      steps: workflowData.steps.map((step) => {
        if (step.id === selectedNode) {
          return {
            ...step,
            action_name: nodeData.action_name,
            status: nodeData.status,
            self_actionable: nodeData.self_actionable,
            userGroupIds: userGroupIds.map((group) => group.value),
            step_inputs: nodeData.step_inputs.map((input) => ({
              ...input,
              type:
                inputTypeList.find(
                  (option) => option.value === input.input_type_id
                )?.input_key || "",
            })),
          };
        }
        return step;
      }),
    };

    setWorkflowData(updatedWorkflowData);
    setToast({
      variant: "filled",
      open: true,
      severity: "success",
      message: t("workflowData.stepUpdatedToast"),
    });
    closeModal();
  };

  // Function to add a child node (new step)
  const addChildNode = () => {
    if (!selectedNode) return;

    const newChildId = uuidv4();
    const parentNode = nodes.find((node) => node.id === selectedNode);

    const newNode = {
      id: newChildId,
      type: "default",
      position: {
        x: parentNode.position.x,
        y: parentNode.position.y + 100,
      },
      style: {
        background: "transparent",
        border: "none",
        width: 240,
        height: 120,
        zIndex: 1,
      },
      data: {
        label: (
          <StyledNode isDarkMode={isDarkMode}>
            <div>
              <strong>
                {t("workflowData.actionLabel", {
                  actionName:
                    nodeData.action_name || t("workflowData.unknownLabel"),
                })}
              </strong>
              <p>
                {t("workflowData.statusLabel", {
                  status:
                    nodeData.status || t("workflowData.unknownStatusLabel"),
                })}
              </p>
            </div>
          </StyledNode>
        ),
      },
    };

    const targetActionName =
      nodeData.action_name || t("workflowData.unknownLabel");

    const newEdge = {
      id: `edge-${selectedNode}-${newChildId}`,
      source: selectedNode,
      target: newChildId,
      label: t("workflowData.toActionLabel", { actionName: targetActionName }),
    };

    // Process step inputs with proper position values
    const stepInputs = (nodeData.step_inputs || []).map((input, index) => {
      const inputType = inputTypeList.find(
        (option) => option.value === input.input_type_id
      );
      return {
        ...input,
        type: inputType?.input_key || "",
        position: input.position ?? index, // Use existing position or index as fallback
        options: input.options || [],
        required: input.required || false,
        default_value: input.default_value || "",
      };
    });

    const updatedWorkflowData = {
      ...workflowData,
      steps: [
        ...workflowData.steps,
        {
          id: newChildId,
          action_name: nodeData.action_name,
          parent_id: selectedNode,
          status: nodeData.status,
          self_actionable: nodeData.self_actionable,
          userGroupIds: userGroupIds.map((group) => group.value),
          step_inputs: stepInputs,
        },
      ],
    };

    setNodes((prevNodes) => [...prevNodes, newNode]);
    setEdges((prevEdges) => [...prevEdges, newEdge]);
    setWorkflowData(updatedWorkflowData);
    setToast({
      variant: "filled",
      open: true,
      severity: "success",
      message: t("workflowData.stepCreatedToast"),
    });
    closeModal();
  };

  // Function to delete a node and its associated edges
  const deleteNode = (nodeId) => {
    // Find the step being deleted and its parent
    const deletedStep = workflowData.steps.find((step) => step.id === nodeId);
    if (!deletedStep) return;

    const parentId = deletedStep.parent_id || workflowData.id;
    const children = workflowData.steps.filter(
      (step) => step.parent_id === nodeId
    );

    // Update workflow data to reassign children to parent
    setWorkflowData((prevData) => ({
      ...prevData,
      steps: prevData.steps
        .map((step) =>
          children.some((child) => child.id === step.id)
            ? { ...step, parent_id: parentId }
            : step
        )
        .filter((step) => step.id !== nodeId),
    }));

    // Update edges to reconnect children to parent
    setEdges((prevEdges) => {
      const filteredEdges = prevEdges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      );
      const newEdges = children.map((child) => ({
        id: `edge-${parentId}-${child.id}`,
        source: parentId,
        target: child.id,
        label: t("workflowData.actionLabel", {
          actionName: child.action_name || t("workflowData.unknownLabel"),
        }),
      }));
      return [...filteredEdges, ...newEdges];
    });

    // Update nodes
    setNodes((prevNodes) => prevNodes.filter((node) => node.id !== nodeId));

    setToast({
      variant: "filled",
      open: true,
      severity: "info",
      message: t("workflowData.stepDeletedToast"),
    });
  };

  // Connection handling for ReactFlow
  const onConnect = useCallback(
    (connection) =>
      setEdges((prevEdges) =>
        addEdge({ ...connection, animated: true }, prevEdges)
      ),
    [setEdges]
  );

  // Handling workflow name changes
  const changeHandlerName = (e) => {
    const newName = e.target.value;
    setWorkflowData((prevData) => ({
      ...prevData,
      name: newName,
    }));
  };

  useEffect(() => {
    const nodes = [];
    const edges = [];
    let currentY = 0;

    const parentStepsMap = new Map();

    workflowData.steps.forEach((step) => {
      const parentId = step.parent_id || "root";
      if (!parentStepsMap.has(parentId)) {
        parentStepsMap.set(parentId, []);
      }
      parentStepsMap.get(parentId).push(step);
    });

    const containerWidth = 1600;
    const nodeWidth = 200;
    const horizontalSpacing = 220;

    parentStepsMap.forEach((parentSteps) => {
      const totalSteps = parentSteps.length;
      let currentX =
        (containerWidth - totalSteps * (nodeWidth + horizontalSpacing)) / 2;

      parentSteps.forEach((step) => {
        const nodeId = step.id;

        nodes.push({
          id: nodeId,
          type: "default",
          position: { x: currentX, y: currentY },
          style: {
            background: "transparent",
            border: "none",
            width: 240,
            height: 120,
            zIndex: 1,
          },
          data: {
            label: (
              <StyledNode isDarkMode={isDarkMode}>
                {step.parent_id && (
                  <IconButton
                    onClick={() =>
                      onDeleteStep ? onDeleteStep(nodeId) : deleteNode(nodeId)
                    }
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: theme.palette.error.main,
                      color: "#fff",
                      "&:hover": { bgcolor: theme.palette.error.dark },
                      zIndex: 2,
                    }}
                    aria-label={t("workflowData.deleteNodeAria", {
                      status:
                        step.status || t("workflowData.unknownStatusLabel"),
                    })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <Box
                  onClick={() => openUpdateModal(nodeId)}
                  sx={{
                    height: "100%",
                    width: "100%",
                    px: 1.5,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? theme.palette.grey[900]
                        : theme.palette.grey[100],
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    align="center"
                  >
                    {t("workflowData.statusLabel", {
                      status:
                        step.status || t("workflowData.unknownStatusLabel"),
                    })}
                  </Typography>
                  {step.self_actionable && (
                    <Chip
                      label={t("workflowData.selfActionableLabel")}
                      color="success"
                      size="small"
                      icon={<CheckCircleIcon />}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Tooltip title={t("workflowData.addStepTooltip")} arrow>
                    <IconButton
                      onClick={() => openModal(nodeId)}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.success.main,
                        color: "#fff",
                        "&:hover": { bgcolor: theme.palette.success.dark },
                      }}
                      aria-label={t("workflowData.addStepAria", {
                        status:
                          step.status || t("workflowData.unknownStatusLabel"),
                      })}
                    >
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledNode>
            ),
          },
        });

        if (step.parent_id) {
          const targetNode = workflowData.steps.find((s) => s.id === nodeId);
          edges.push({
            id: `edge-${step.parent_id}-${nodeId}`,
            source: step.parent_id,
            target: nodeId,
            label: t("workflowData.actionLabel", {
              actionName:
                targetNode?.action_name || t("workflowData.unknownLabel"),
            }),
          });
        }

        currentX += nodeWidth + horizontalSpacing;
      });

      currentY += 180;
    });

    setNodes(nodes);
    setEdges(edges);
  }, [workflowData, isDarkMode, t]);

  // Helper function to process default_value based on input type
  const processDefaultValue = (defaultValue, inputTypeKey) => {
    // If no default value provided, return null
    if (!defaultValue || defaultValue === "") {
      return null;
    }

    const key = inputTypeKey?.toLowerCase();

    switch (key) {
      case "short_text":
      case "text":
        // Text types: keep as string
        return defaultValue;

      case "numeric": {
        // Numeric: convert to number, return null if not a valid number
        const numValue = Number(defaultValue);
        return isNaN(numValue) ? null : numValue;
      }

      case "boolean": {
        // Boolean: convert to actual boolean
        if (typeof defaultValue === "boolean") {
          return defaultValue;
        }
        const strValue = String(defaultValue).toLowerCase();
        return strValue === "true" || strValue === "yes" || strValue === "1";
      }

      case "confirmation":
      case "file_upload":
      case "timestamp":
      case "selection":
      case "multi_selection":
      case "user_selection":
      case "multi_user_selection":
        // These types: always null (no default values)
        return null;

      default:
        return null;
    }
  };

  // Derive backend data from workflowData
  const handleSubmit = async (e) => {
    e.preventDefault();
    const sentData = {
      name: workflowData.name,
      target: workflowData.target,
      steps: workflowData.steps.map((step) => {
        // Ensure step inputs have proper position values
        const stepInputs = (step.step_inputs || []).map((input, index) => {
          const inputType = inputTypeList.find(
            (option) => option.value === input.input_type_id
          );
          const processedDefaultValue = processDefaultValue(
            input.default_value,
            inputType?.input_key
          );

          return {
            input_type_id: input.input_type_id,
            type: inputType?.input_key || "",
            name: input.name || "",
            description: input.description || "",
            position: input.position ?? index, // Use existing position or index as fallback
            required: input.required || false,
            default_value: processedDefaultValue,
            options: input.options || [],
          };
        });

        return {
          id: step.id,
          parent_id: step.parent_id,
          action_name: step.action_name,
          status: step.status,
          self_actionable: step.self_actionable,
          user_group_ids: step.userGroupIds,
          step_inputs: stepInputs,
        };
      }),
    };
    const url = "/api/v1/org-admin/workflows";
    try {
      await postData(
        { url, data: sentData },
        {
          onSuccess: () => {
            setWorkflowData(defaultWorkFlow);
            refetchWorkflows();
            setToast({
              open: true,
              severity: "success",
              message: t("workflowData.workflowCreatedToast"),
            });
          },
          onError: (err) => {
            handleApiError(err);
          },
          onSettled: () => {
            refetchWorkflows();
          },
        }
      );
    } catch (err) {
      handleApiError(err);
    }
  };

  return {
    postIsLoading,
    postError,
    nodeData,
    setNodeData,
    nodes,
    onNodesChange,
    edges,
    onEdgesChange,
    isModalOpen,
    userGroupIds,
    modalButton,
    workflowData,
    userGroupsList,
    closeModal,
    updateNodeData,
    addChildNode,
    deleteNode,
    onConnect,
    changeHandlerName,
    changeTarget,
    handleSubmit,
    handleMultiChange,
    toast,
    handleToastClose,
    isDarkMode,
    inputTypeList,
  };
};

export default useWorkflowData;
